import type { ViewStyle } from 'react-native'
import { TURN_CARD_SPACING, TURN_PANEL_COLORS } from '../lib/turnPanelAppearance'
import { DESKTOP_BREAKPOINT } from '../lib/mapDimensions'

export interface TurnPanelConcept {
  id: string
  name: string
  summary: string
  detail: string
  panel: ViewStyle
  accent: string
  css: string
  inset?: boolean
  ruled?: boolean
  ruleSpacing?: number
  controls?: ViewStyle
  controlsCss?: string
  controlsShadow?: { light: string; dark: string }
  controlSpacing?: { paddingY: number; placementGap: number; actionsTop: number }
}

export const TURN_PANEL_CONCEPTS: readonly TurnPanelConcept[] = [
  { id: 'olive-frame', name: 'Olive frame', summary: 'A fine olive outline around the whole turn area.', detail: 'Closest to the existing UI. The frame gives the panel an edge while keeping the map and actions quiet.', panel: { backgroundColor: '#eee3c1', borderColor: '#738348', borderWidth: 1, borderRadius: 12 }, accent: '#738348', css: 'border-radius:12px;margin:8px 0;overflow:hidden;background:var(--concept-canvas);box-shadow:inset 0 0 0 1px var(--concept-olive);' },
  { id: 'paper-tray', name: 'Paper tray', summary: 'A warmer paper surface, capped by a stronger rule.', detail: 'A clear change in surface below the toolbar. Minimal ornament, with the controls still sitting on the shared right margin.', panel: { backgroundColor: '#f6efd9', borderTopWidth: 4, borderColor: '#738348', borderRadius: 4 }, accent: '#738348', css: 'background:var(--concept-paper);border-top:4px solid var(--concept-olive);' },
  { id: 'inset-well', name: 'Inset well', summary: 'A recessed panel with a deeper background and soft edge.', detail: 'The turn controls feel contained in a shallow well. The darker fill separates the whole area from the board above.', panel: { backgroundColor: '#e2d6b2', borderWidth: 1, borderColor: '#c1b58e', borderRadius: 10 }, accent: '#b5a675', css: 'background:var(--concept-well);margin:8px 0;border-radius:10px;box-shadow:inset 0 0 0 1px var(--concept-border),inset 0 2px 8px #00000018;overflow:hidden;' },
  { id: 'field-notes', name: 'Field notes', summary: 'A left binding strip and faint rules across the paper.', detail: 'A field notebook treatment: subtle horizontal lines make the empty space feel deliberate, and the binding gives it a strong left edge.', panel: { backgroundColor: '#f4ecd3', borderLeftWidth: 5, borderColor: '#738348' }, accent: '#738348', css: 'border-left:5px solid var(--concept-olive);background-color:var(--concept-paper);background-image:repeating-linear-gradient(transparent,transparent 27px,var(--concept-rule) 28px);' },
  { id: 'command-rail', name: 'Command rail', summary: 'A dark olive header above a quieter paper body.', detail: 'The phase counter and next action sit in a defined command band. Stronger separation, especially when there are few controls in the panel.', panel: { backgroundColor: '#f4ecd3', borderTopWidth: 34, borderColor: '#46532b', borderRadius: 6 }, accent: '#46532b', css: 'background:var(--concept-paper);border-top:3px solid var(--concept-olive);' },
  { id: 'compact-card', name: 'Compact card', summary: 'Frame just the action row; leave the rest of the area open.', detail: 'The counter and action buttons become one compact card. It removes the sense of a large empty panel without moving the map or toolbar.', panel: { backgroundColor: '#eee3c1' }, accent: '#738348', css: 'background:var(--concept-canvas);' },
]

const insetWell = TURN_PANEL_CONCEPTS.find(concept => concept.id === 'inset-well')!
const fieldNotes = TURN_PANEL_CONCEPTS.find(concept => concept.id === 'field-notes')!
export const TURN_PANEL_ROUND_TWO: readonly TurnPanelConcept[] = [
  insetWell,
  fieldNotes,
  {
    id: 'bound-well', name: 'Bound well',
    summary: 'The recessed well with a notebook binding and faint rules.',
    detail: 'Combines the depth of Inset well with the left binding and quiet ruling of Field notes. The olive spine gives the turn area a clear anchor.',
    panel: { backgroundColor: '#e2d6b2', borderLeftWidth: 5, borderColor: '#738348', borderRadius: 10 },
    accent: '#738348', inset: true, ruled: true,
    css: 'background-color:var(--concept-well);border-left:5px solid var(--concept-olive);border-radius:10px;margin:8px 0;box-shadow:inset 0 2px 8px #00000018;overflow:hidden;background-image:repeating-linear-gradient(transparent,transparent 27px,var(--concept-rule) 28px);',
  },
  {
    id: 'framed-notes', name: 'Framed notes',
    summary: 'Ruled paper held inside a rounded olive frame.',
    detail: 'Keeps the notebook texture, with a fine frame around every edge. A lighter paper surface makes the controls feel like a separate sheet under the map.',
    panel: { backgroundColor: '#f6efd9', borderWidth: 1, borderColor: '#738348', borderRadius: 12 },
    accent: '#738348', inset: true, ruled: true,
    css: 'background-color:var(--concept-paper);border-radius:12px;margin:8px 0;box-shadow:inset 0 0 0 1px var(--concept-olive),0 2px 0 #0000000b;overflow:hidden;background-image:repeating-linear-gradient(transparent,transparent 27px,var(--concept-rule) 28px);',
  },
  {
    id: 'wide-rule-notes', name: 'Wide-rule notes',
    summary: 'More breathing room between rules, with an olive top edge.',
    detail: 'A calmer notebook variation: wider line spacing, a slim left binding and a strong top edge. The paper stretches across the full turn area.',
    panel: { backgroundColor: '#f6efd9', borderTopWidth: 4, borderLeftWidth: 2, borderColor: '#738348' },
    accent: '#738348', ruled: true, ruleSpacing: 40,
    css: 'background-color:var(--concept-paper);border-top:4px solid var(--concept-olive);border-left:2px solid var(--concept-border);background-image:repeating-linear-gradient(transparent,transparent 39px,var(--concept-rule) 40px);',
  },
  {
    id: 'layered-well', name: 'Layered well',
    summary: 'A deeper recess with a narrow raised rim around it.',
    detail: 'Builds on Inset well with a second inner edge. The raised rim and deeper fill give the controls a stronger container, with no notebook rules.',
    panel: { backgroundColor: '#d8cba4', borderWidth: 4, borderColor: '#f6efd9', borderRadius: 14 },
    accent: '#b5a675', inset: true,
    css: 'background:var(--concept-deep-well);border-radius:14px;margin:8px 0;overflow:hidden;box-shadow:inset 0 0 0 1px var(--concept-border),inset 0 0 0 4px var(--concept-paper),inset 0 6px 12px #00000020;',
  },
]

export const TURN_PANEL_ROUND_THREE: readonly TurnPanelConcept[] = [
  {
    id: 'open-well', name: 'Open well',
    summary: 'The deep well, with the large surrounding box removed.',
    detail: 'A simpler version of Layered well: one uninterrupted background, with the brighter rim kept only around the active controls.',
    panel: { backgroundColor: '#d8cba4' }, accent: '#738348',
    css: 'background:var(--concept-deep-well);',
    controls: { backgroundColor: '#f6efd9', borderWidth: 1, borderColor: '#738348', borderRadius: 10 },
    controlsCss: 'background:var(--concept-paper) !important;border:1px solid var(--concept-olive);border-radius:10px;box-shadow:inset 0 1px 0 var(--concept-highlight);',
  },
  {
    id: 'highlight-tray', name: 'Highlight tray',
    summary: 'Paper tray’s top line, with a contrasting control card.',
    detail: 'A clean olive rule separates the turn area from the map bar. The deeper tray sits behind a lighter card with a crisp olive edge.',
    panel: { backgroundColor: '#e2d6b2', borderTopWidth: 3, borderColor: '#738348' }, accent: '#738348',
    css: 'background:var(--concept-well);border-top:3px solid var(--concept-olive);',
    controls: { backgroundColor: '#f6efd9', borderWidth: 1, borderTopWidth: 3, borderColor: '#738348', borderRadius: 8 },
    controlsCss: 'background:var(--concept-paper) !important;border:1px solid var(--concept-olive);border-top-width:3px;border-radius:8px;',
  },
  {
    id: 'lifted-controls', name: 'Lifted controls',
    summary: 'A light control card with a small raised edge.',
    detail: 'A shallow three-dimensional card: a bright top edge, firm border and narrow shadow underneath. The rest of the turn area stays flat.',
    panel: { backgroundColor: '#e2d6b2' }, accent: '#738348',
    css: 'background:var(--concept-well);',
    controls: { backgroundColor: '#f6efd9', borderWidth: 1, borderBottomWidth: 4, borderColor: '#738348', borderRadius: 10 },
    controlsCss: 'background:var(--concept-paper) !important;border:1px solid var(--concept-olive);border-radius:10px;box-shadow:inset 0 2px 0 var(--concept-highlight),0 3px 0 var(--concept-border),0 5px 8px #00000015;',
  },
  {
    id: 'pressed-controls', name: 'Pressed controls',
    summary: 'A recessed control card on a lighter, flat background.',
    detail: 'Reverses the contrast: the turn area is light paper and the controls sit in a darker shallow recess. One clear border contains the active choices.',
    panel: { backgroundColor: '#f6efd9' }, accent: '#738348',
    css: 'background:var(--concept-paper);',
    controls: { backgroundColor: '#e2d6b2', borderWidth: 1, borderColor: '#738348', borderRadius: 8 },
    controlsCss: 'background:var(--concept-well) !important;border:1px solid var(--concept-olive);border-radius:8px;box-shadow:inset 0 2px 5px #00000020,0 1px 0 var(--concept-highlight);',
  },
  {
    id: 'olive-sheet', name: 'Olive sheet',
    summary: 'A muted olive background under a warm paper card.',
    detail: 'Two quiet surfaces with a little more color contrast. The olive sheet runs edge to edge; the control card has a fine double edge without framing the whole panel.',
    panel: { backgroundColor: '#d5d9bd' }, accent: '#738348',
    css: 'background:var(--concept-sheet);',
    controls: { backgroundColor: '#f6efd9', borderWidth: 2, borderColor: '#738348', borderRadius: 10 },
    controlsCss: 'background:var(--concept-paper) !important;border:1px solid var(--concept-olive);border-radius:10px;box-shadow:inset 0 0 0 2px var(--concept-paper),inset 0 0 0 3px var(--concept-border);',
  },
  {
    id: 'hairline-tray', name: 'Hairline tray',
    summary: 'A single dividing line and a sharper, minimal card.',
    detail: 'The lightest treatment: a thin olive line beneath the toolbar, a deeper flat background and a small-radius control card with a stronger border.',
    panel: { backgroundColor: '#e2d6b2', borderTopWidth: 1, borderColor: '#738348' }, accent: '#738348',
    css: 'background:var(--concept-well);border-top:1px solid var(--concept-olive);',
    controls: { backgroundColor: '#f6efd9', borderWidth: 2, borderColor: '#738348', borderRadius: 3 },
    controlsCss: 'background:var(--concept-paper) !important;border:2px solid var(--concept-olive);border-radius:3px;',
  },
  {
    id: 'clean-field-notes', name: 'Clean field notes',
    summary: 'Notebook lines around a solid, unruled control card.',
    detail: 'The Field notes binding and paper rules stay in the surrounding background. A solid paper card keeps the placement, attack and fortify controls clear of lines.',
    panel: { backgroundColor: '#e2d6b2', borderLeftWidth: 4, borderColor: '#738348' }, accent: '#738348', ruled: true,
    css: 'background-color:var(--concept-well);border-left:4px solid var(--concept-olive);background-image:repeating-linear-gradient(transparent,transparent 27px,var(--concept-rule) 28px);',
    controls: { backgroundColor: '#f6efd9', borderWidth: 1, borderColor: '#738348', borderRadius: 10 },
    controlsCss: 'background:var(--concept-paper) !important;border:1px solid var(--concept-olive);border-radius:10px;',
  },
  {
    id: 'ruled-tray', name: 'Ruled tray',
    summary: 'A top highlight and wider rules, behind a lifted solid card.',
    detail: 'Combines Paper tray’s highlight line with wider notebook ruling. The bright control card has a shallow raised edge and an opaque background, so the lines stop at its border.',
    panel: { backgroundColor: '#e2d6b2', borderTopWidth: 3, borderColor: '#738348' }, accent: '#738348', ruled: true, ruleSpacing: 40,
    css: 'background-color:var(--concept-well);border-top:3px solid var(--concept-olive);background-image:repeating-linear-gradient(transparent,transparent 39px,var(--concept-rule) 40px);',
    controls: { backgroundColor: '#f6efd9', borderWidth: 1, borderBottomWidth: 3, borderColor: '#738348', borderRadius: 8 },
    controlsCss: 'background:var(--concept-paper) !important;border:1px solid var(--concept-olive);border-radius:8px;box-shadow:inset 0 1px 0 var(--concept-highlight),0 2px 0 var(--concept-border);',
  },
  {
    id: 'beveled-controls', name: 'Beveled controls',
    summary: 'A stronger rim and a visible three-dimensional lower edge.',
    detail: 'The control box has a bold olive rim, a bright inner top edge and a solid lower ledge. A deeper background makes the raised surface stand out in both themes.',
    panel: { backgroundColor: '#d8cba4' }, accent: '#738348',
    css: 'background:var(--concept-deep-well);',
    controls: { backgroundColor: '#f6efd9', borderWidth: 2, borderBottomWidth: 6, borderColor: '#738348', borderRadius: 10 },
    controlsCss: 'background:var(--concept-raised-paper) !important;border:2px solid var(--concept-olive);border-radius:10px;box-shadow:inset 0 3px 0 var(--concept-bright-edge),inset 0 -2px 0 #00000010,0 4px 0 var(--concept-ledge),0 7px 10px #00000030;',
  },
  {
    id: 'floating-controls', name: 'Floating controls',
    summary: 'A brighter card with a deeper soft shadow below it.',
    detail: 'A light control surface floats above the darker sheet. A crisp border and broad downward shadow create more separation without adding another frame around the turn area.',
    panel: { backgroundColor: '#e2d6b2' }, accent: '#738348',
    css: 'background:var(--concept-well);',
    controls: { backgroundColor: '#fff9e9', borderWidth: 2, borderColor: '#738348', borderRadius: 12, boxShadow: '0 6px 12px #00000035' },
    controlsCss: 'background:var(--concept-raised-paper) !important;border:2px solid var(--concept-olive);border-radius:12px;box-shadow:inset 0 1px 0 var(--concept-bright-edge),0 6px 14px #00000045;',
  },
  {
    id: 'offset-controls', name: 'Offset controls',
    summary: 'A firm olive edge offset behind the control box.',
    detail: 'A graphic raised card with a solid olive shadow to the right and below. The warm face and bright inner edge keep the active controls distinct from the flat background.',
    panel: { backgroundColor: '#d5d9bd' }, accent: '#738348',
    css: 'background:var(--concept-sheet);',
    controls: { backgroundColor: '#fff9e9', borderWidth: 2, borderColor: '#738348', borderRadius: 6, boxShadow: '3px 4px 0 #46532b' },
    controlsCss: 'background:var(--concept-raised-paper) !important;border:2px solid var(--concept-olive);border-radius:6px;box-shadow:inset 0 2px 0 var(--concept-bright-edge),3px 4px 0 var(--concept-ledge);',
  },
]

const openWell = TURN_PANEL_ROUND_THREE.find(concept => concept.id === 'open-well')!
const floatingControls = TURN_PANEL_ROUND_THREE.find(concept => concept.id === 'floating-controls')!
const firmWell: TurnPanelConcept = {
  ...openWell, id: 'firm-well', name: 'Firm well',
  summary: 'Open well with a slightly thicker control-card border.',
  detail: 'The same open background and paper surface as Open well, with the control-card outline increased from 1px to 2px. The large surrounding frame stays removed.',
  controls: { ...openWell.controls, borderWidth: 2 },
  controlsCss: 'background:var(--concept-paper) !important;border:2px solid var(--concept-olive);border-radius:10px;box-shadow:inset 0 1px 0 var(--concept-highlight);',
}
export const TURN_PANEL_ROUND_FOUR: readonly TurnPanelConcept[] = [
  openWell,
  floatingControls,
  firmWell,
  {
    id: 'soft-float', name: 'Soft float',
    summary: 'Floating controls with a much softer fading outer glow.',
    detail: 'Keeps the bright face and 2px border of Floating controls. Low-opacity olive shadows fade gradually outward, replacing the stronger dark drop shadow.',
    panel: { backgroundColor: '#e2d6b2' }, accent: '#738348',
    css: 'background:var(--concept-well);',
    controls: { backgroundColor: '#fff9e9', borderWidth: 2, borderColor: '#738348', borderRadius: 12 },
    controlsCss: 'background:var(--concept-raised-paper) !important;border:2px solid var(--concept-olive);border-radius:12px;',
    controlsShadow: {
      light: 'inset 0 1px 0 #ffffff88,0 2px 6px #46532b12,0 5px 14px #46532b08',
      dark: 'inset 0 1px 0 #a5b86c33,0 2px 6px #b3c96a0e,0 5px 14px #b3c96a06',
    },
  },
  {
    id: 'double-edge-well', name: 'Double-edge well',
    summary: 'Open well with two fine edges around the control card.',
    detail: 'A precise variation on Open well: the outer olive outline has a softer inner keyline, with slightly tighter corners. The open background stays flat and unframed.',
    panel: { backgroundColor: '#d8cba4' }, accent: '#738348',
    css: 'background:var(--concept-deep-well);',
    controls: { backgroundColor: '#f6efd9', borderWidth: 1, borderColor: '#738348', borderRadius: 8 },
    controlsCss: 'background:var(--concept-paper) !important;border:1px solid var(--concept-olive);border-radius:8px;',
    controlsShadow: {
      light: 'inset 0 0 0 2px #f6efd9,inset 0 0 0 3px #b5a675',
      dark: 'inset 0 0 0 2px #303923,inset 0 0 0 3px #525f37',
    },
  },
  {
    id: 'diffused-float', name: 'Diffused float',
    summary: 'A finer border with a broad, faint halo beneath the card.',
    detail: 'A quieter floating surface with a 1px outline. A small shadow gives it lift, while a wide, low-opacity halo fades into the open background below.',
    panel: { backgroundColor: '#d8cba4' }, accent: '#738348',
    css: 'background:var(--concept-deep-well);',
    controls: { backgroundColor: '#fff9e9', borderWidth: 1, borderColor: '#738348', borderRadius: 12 },
    controlsCss: 'background:var(--concept-raised-paper) !important;border:1px solid var(--concept-olive);border-radius:12px;',
    controlsShadow: {
      light: 'inset 0 1px 0 #ffffff88,0 2px 4px #00000012,0 8px 18px #46532b14,0 14px 30px #46532b07',
      dark: 'inset 0 1px 0 #a5b86c33,0 2px 4px #00000018,0 8px 18px #b3c96a12,0 14px 30px #b3c96a07',
    },
  },
  {
    ...firmWell, id: 'firm-well-relaxed', name: 'Firm well · relaxed',
    summary: 'A small increase in padding and space above the bottom buttons.',
    detail: 'Firm well with 10px top and bottom padding instead of 8px, 10px between rows, and an extra 2px before Reset, Undo and Commit. A small amount of breathing room.',
    controlSpacing: { paddingY: TURN_CARD_SPACING.paddingY, placementGap: TURN_CARD_SPACING.placementGap, actionsTop: TURN_CARD_SPACING.placementActionsTop },
  },
  {
    ...firmWell, id: 'firm-well-airy', name: 'Firm well · airy',
    summary: 'A little more room above and below Reset, Undo and Commit.',
    detail: 'Firm well with 12px top and bottom padding, 10px between rows, and an extra 4px before the bottom buttons. Still compact, with slightly more space around the actions.',
    controlSpacing: { paddingY: 12, placementGap: 10, actionsTop: 4 },
  },
]

export type TurnPanelRound = 1 | 2 | 3 | 4
export function turnPanelGallery(round: TurnPanelRound): readonly TurnPanelConcept[] {
  return round === 4 ? TURN_PANEL_ROUND_FOUR : round === 3 ? TURN_PANEL_ROUND_THREE : round === 2 ? TURN_PANEL_ROUND_TWO : TURN_PANEL_CONCEPTS
}
export function turnPanelConcept(id: string | undefined): TurnPanelConcept | undefined {
  return [...TURN_PANEL_CONCEPTS, ...TURN_PANEL_ROUND_TWO, ...TURN_PANEL_ROUND_THREE, ...TURN_PANEL_ROUND_FOUR].find(concept => concept.id === id)
}
export function conceptThumbnailStyle(concept: TurnPanelConcept): ViewStyle {
  return concept.panel
}

/** Used only inside the dev fixture route. No styles reach ordinary games. */
function overrideInlineStyle(css: string): string {
  return css.split(';').filter(Boolean).map(declaration => declaration.includes('!important') ? declaration : `${declaration} !important`).join(';') + ';'
}

export function turnPanelConceptCss(concept: TurnPanelConcept, dark: boolean, scopeSelector = '[data-testid="turn-phase-panel"]'): string {
  const panel = scopeSelector
  const row = '[data-testid="turn-phase-action-row"]'
  const colors = TURN_PANEL_COLORS[dark ? 'dark' : 'light']
  return `
    ${panel} {
      --concept-olive:${colors.border};
      --concept-canvas:${dark ? '#1c2111' : '#eee3c1'};
      --concept-paper:${colors.card};
      --concept-well:${dark ? '#141a0e' : '#e2d6b2'};
      --concept-deep-well:${colors.background};
      --concept-border:${dark ? '#525f37' : '#b5a675'};
      --concept-rule:${dark ? '#8aa14a18' : '#73834818'};
      --concept-sheet:${dark ? '#222e18' : '#d5d9bd'};
      --concept-highlight:${colors.highlight};
      --concept-raised-paper:${dark ? '#3d492d' : '#fff9e9'};
      --concept-bright-edge:${dark ? '#a5b86c66' : '#ffffffcc'};
      --concept-ledge:${dark ? '#0b1006' : '#46532b'};
      box-sizing:border-box;min-width:0;${overrideInlineStyle(concept.css)}
    }
    ${panel} [class*="bg-canvas"] { background-color:transparent !important; }
    /* Preserve the earlier samples' spacing after the winning design ships. */
    ${panel} [data-testid="turn-control-card"] {padding:8px 12px !important;}
    ${panel} [aria-label="Placement controls"] {gap:8px !important;}
    ${panel} [data-testid="placement-actions"] {margin-top:0 !important;}
    ${panel} ${row} {padding-top:6px !important;padding-bottom:6px !important;}
    ${concept.ruled || concept.id === 'field-notes' ? `
      /* Ruling belongs to the sheet behind the cards, never the controls. */
      ${panel} ${row}, ${panel} [data-testid="turn-control-card"] {
        background-color:var(${concept.controls ? '--concept-paper' : concept.panel.backgroundColor === '#e2d6b2' ? '--concept-well' : '--concept-paper'}) !important;
        background-image:none !important;
      }
    ` : ''}
    ${concept.controlsCss ? `
      ${panel} ${row}, ${panel} [data-testid="turn-control-card"] {${overrideInlineStyle(concept.controlsCss)}${concept.controlsShadow ? `box-shadow:${concept.controlsShadow[dark ? 'dark' : 'light']} !important;` : ''}}
      /* The card expands around the row while retaining its left counter inset. */
      ${panel} ${row} {margin:0 ${TURN_CARD_SPACING.rowMargin}px !important;padding:8px ${TURN_CARD_SPACING.rowPaddingLeft}px !important;}
      /* Keep special-dice and conquest accents visible on their existing cards. */
      ${panel} .turn-card-blood {border-color:${dark ? '#e0463f' : '#b82020'} !important;}
      ${panel} .turn-card-transfer {border-color:${dark ? '#fbbf24' : '#b45309'} !important;}
    ` : ''}
    ${concept.id === 'command-rail' ? `
      ${panel} ${row} {background:#46532b !important;border-radius:6px;padding:10px 8px !important;margin:0 -8px;}
      ${panel} ${row} [class*="text-"] {color:#f4ecd3 !important;}
      ${panel} ${row} button {border:1px solid #8aa14a;}
      ${panel} [aria-label="Placement controls"] > div:first-child {background:#46532b;padding:10px 8px;margin:-8px -12px 0;}
      ${panel} [aria-label="Placement controls"] > div:first-child [class*="text-"] {color:#f4ecd3 !important;}
    ` : ''}
    ${concept.id === 'compact-card' ? `
      ${panel} ${row} {background:var(--concept-paper) !important;border:0;border-radius:10px;padding:12px 8px !important;margin:0 -8px;box-shadow:inset 0 0 0 1px var(--concept-olive),0 3px 0 #0000000b;}
      ${panel} [aria-label="Placement controls"] {background:var(--concept-paper) !important;border-radius:10px;box-shadow:0 3px 0 #0000000b;}
    ` : ''}
    /* All mocks leave breathing room between the action and the card's right edge. */
    ${panel} ${row} {margin-left:${TURN_CARD_SPACING.rowMargin}px !important;margin-right:${TURN_CARD_SPACING.rowMargin}px !important;padding-right:${TURN_CARD_SPACING.rowPaddingRight}px !important;}
    ${concept.controlSpacing ? `
      ${panel} ${row}, ${panel} [data-testid="turn-control-card"] {
        padding-top:${concept.controlSpacing.paddingY}px !important;
        padding-bottom:${concept.controlSpacing.paddingY}px !important;
      }
      ${panel} [aria-label="Placement controls"] {row-gap:${concept.controlSpacing.placementGap}px !important;}
      ${panel} [data-testid="placement-actions"] {margin-top:${concept.controlSpacing.actionsTop}px !important;}
    ` : ''}
    @media (min-width:${DESKTOP_BREAKPOINT}px) {
      ${panel} ${row}, ${panel} [data-testid="turn-control-card"] {
        margin-left:${TURN_CARD_SPACING.desktopMargin}px !important;
        margin-right:${TURN_CARD_SPACING.desktopMargin}px !important;
      }
    }
  `
}
